import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import LifeDetailPageV2 from "./LifeDetailPageV2";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import PatientImageCard from "../../components/common/MedicalCampaignDetailPageComponents/PatientImageCard";
import CampaignProgress from "../../components/common/CampaignDetailPageComponents/CampaignProgress";
import PatientInfoSection from "../../components/common/MedicalCampaignDetailPageComponents/PatientInfoSection";
import CampaignTopBar from "../../components/common/CampaignDetailPageComponents/CampaignTopBar";
import CampaignSidebar from "../../components/common/CampaignDetailPageComponents/CampaignSidebar";
import HowToDonate from "../../components/common/CampaignDetailPageComponents/HowToDonate";
import MedicalStory from "../../components/common/MedicalCampaignDetailPageComponents/MedicalStory";
import DocumentSection from "../../components/common/CampaignDetailPageComponents/Document";
import Updates from "../../components/common/CampaignDetailPageComponents/Updates";
import Comments from "../../components/common/CampaignDetailPageComponents/Comments";
import Donors from "../../components/common/CampaignDetailPageComponents/Donors";
import ReferFriend from "../../components/common/CampaignDetailPageComponents/ReferFriend";
import UrgentNeedsCampaign from "../../components/common/MedicalCampaignDetailPageComponents/UrgentNeedsCampaign";
import Faq from "../../components/common/CampaignDetailPageComponents/Faq";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import CampaignPopup from "../../components/common/CampaignDetailPageComponents/CampaignPopup";
import ClosedCampaign from "../../components/common/CampaignDetailPageComponents/ClosedCampaign";
import DonationBox from "../../components/common/DonationBox";
import { getCampaignBySlug } from "../../api/services/campaignService";

const BREADCRUMB_ITEMS = [
  { label: "Life", path: "/life" },
  { label: "Life Detail Page", path: null },
];

const LifeDetailPage = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showPopup, setShowPopup] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donationAmount, setDonationAmount] = useState(3000);

  useEffect(() => {
    const fetchLifeCampaign = async () => {
      if (!slug) {
        setError(new Error("No slug"));
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        // Life route: prefer life campaign if campaign/life share same slug
        const response = await getCampaignBySlug(slug, false, {
          preferLifeCampaign: true,
        });
        const data = response?.data;
        // Ensure this is actually a life campaign
        const isLifeCampaign = Number(data?.featuremedicalCampaign) === 1 || Number(data?.featureLifeCampaign) === 1;
        if (data && isLifeCampaign) {
          setCampaign(data);
        } else {
          setCampaign(null);
          setError(new Error("Life campaign not found"));
        }
      } catch (err) {
        setError(err);
        setCampaign(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLifeCampaign();
  }, [slug]);

  // Live raised amount via SSE — updates progress bar in real-time when QR donations arrive
  useEffect(() => {
    if (!campaign?.id) return;
    const base = import.meta.env.VITE_API_BASE_URL || '';
    const es = new EventSource(`${base}/campaigns/${campaign.id}/live`);
    es.onmessage = (e) => {
      try {
        const { raised } = JSON.parse(e.data);
        if (typeof raised === 'number') {
          setCampaign(prev => prev ? { ...prev, raisedAmount: raised } : prev);
        }
      } catch (_) {}
    };
    return () => es.close();
  }, [campaign?.id]);

  const handleClosePopup = useCallback(() => setShowPopup(false), []);

  const handleSidebarNavigation = useCallback(
    (sectionId) => {
      const findVisibleElement = (id) => {
        const allElements = Array.from(document.querySelectorAll(`#${id}`));
        const visibleElement = allElements.find((el) => {
          const style = window.getComputedStyle(el);
          const rect = el.getBoundingClientRect();
          return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            style.opacity !== "0" &&
            rect.width > 0 &&
            rect.height > 0
          );
        });
        return visibleElement || allElements[0] || document.getElementById(id);
      };

      const attemptScroll = (attempt = 0) => {
        const maxAttempts = 5;
        const delay = attempt * 50;
        setTimeout(() => {
          const element = findVisibleElement(sectionId);
          if (element) {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const rect = element.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0 && attempt < maxAttempts - 1) {
              attemptScroll(attempt + 1);
              return;
            }
            const offset = isMobile ? 100 : 120;
            const elementTop = rect.top + scrollTop;
            const targetScrollPosition = elementTop - offset;
            window.scrollTo({
              top: Math.max(0, targetScrollPosition),
              behavior: "smooth",
            });
          } else if (attempt < maxAttempts - 1) {
            attemptScroll(attempt + 1);
          }
        }, delay);
      };
      requestAnimationFrame(() => attemptScroll(0));
    },
    [isMobile]
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !campaign) {
    return <Navigate to="/404" replace />;
  }

  // EVERY life campaign uses the redesigned layout.
  //
  // This used to be gated on a date (V2_CUTOFF = 15 Jun 2026, added with the
  // original "Life section v2" work in ff48bc6): only campaigns created on or
  // after that date got LifeDetailPageV2, so every campaign created before it
  // still rendered the pre-revamp stacked design below. That is why older life
  // campaigns looked like "old code". The gate is removed.
  //
  // LifeDetailPageV2 handles closed campaigns itself (it returns
  // ClosedCampaign with hideSimilarCampaigns), so the Closedcampaign branch
  // below is covered too.
  //
  // Everything after this return is now unreachable legacy layout — left in
  // place for one release so a specific old campaign can be diffed against it
  // if anything looks wrong, then it should be deleted.
  return <LifeDetailPageV2 campaignData={campaign} />;

  // Closed life campaign — same as small vendor: closed screen only, no urgent/similar section
  if (Number(campaign.Closedcampaign) === 1) {
    return (
      <>
        <Breadcrumb items={BREADCRUMB_ITEMS} />
        <ClosedCampaign campaignData={campaign} hideSimilarCampaigns />
      </>
    );
  }

  const showProgress = (Number(campaign.hideAll) || 0) !== 1;

  return (
    <>
      <Breadcrumb items={BREADCRUMB_ITEMS} />

      <PatientImageCard campaignData={campaign} />

      {showProgress && (
        <Box
          sx={{
            width: "90%",
            maxWidth: "1400px",
            margin: "0 auto",
            marginTop: isMobile ? "-20px" : "-80px",
            zIndex: 1,
            position: "relative",
          }}
        >
          <CampaignProgress campaignData={campaign} />
        </Box>
      )}

      <PatientInfoSection campaignData={campaign} />

      <Box
        sx={{
          width: "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: { xs: "block", "@media (min-width: 1001px)": { display: "none" } },
            marginTop: isMobile ? "0px" : "30px",
            marginBottom: isMobile ? "20px" : "0px",
            position: "sticky",
            top: "10px",
            zIndex: 999,
          }}
        >
          <CampaignTopBar
            onNavigate={handleSidebarNavigation}
            campaignType="medical"
            showPatientHeader={true}
            campaignData={campaign}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", "@media (min-width: 1001px)": { display: "flex" } },
            flexDirection: "row",
            gap: "30px",
            marginTop: "50px",
            marginBottom: "60px",
          }}
        >
          <Box sx={{ width: "23.41%", position: "sticky", top: "20px", alignSelf: "flex-start" }}>
            <CampaignSidebar
              onNavigate={handleSidebarNavigation}
              campaignType="medical"
              showPatientHeader={true}
              campaignData={campaign}
            />
            <Box sx={{ marginTop: "20px" }}>
              <HowToDonate isMedicalPage={true} />
            </Box>
          </Box>

          <Box sx={{ width: "76.59%" }}>
            <Box id="story" sx={{ marginBottom: "30px" }}>
              <MedicalStory
                onStoryButtonClick={undefined}
                showPatientHeader={true}
                showFullContentByDefault={true}
                campaignData={campaign}
              />
            </Box>

            <Box id="document" sx={{ marginBottom: "30px" }}>
              <DocumentSection campaignData={campaign} />
            </Box>

            <Box id="updates" sx={{ marginBottom: "30px" }}>
              <Updates campaignData={campaign} />
            </Box>

            <Box id="comments" sx={{ marginBottom: "30px" }}>
              <Comments campaignData={campaign} />
            </Box>

            <Box sx={{ marginBottom: "30px" }}>
              <Donors campaignData={campaign} />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "block", "@media (min-width: 1001px)": { display: "none" } },
            width: "100%",
            marginBottom: isMobile ? "0px" : "60px",
          }}
        >
          <Box id="story" sx={{ marginTop: "30px", marginBottom: "30px" }}>
            <MedicalStory
              onStoryButtonClick={undefined}
              showPatientHeader={true}
              showFullContentByDefault={true}
              campaignData={campaign}
            />
          </Box>

          <Box id="document" sx={{ marginBottom: "30px" }}>
            <DocumentSection campaignData={campaign} />
          </Box>

          <Box id="updates" sx={{ marginTop: "30px", marginBottom: "30px" }}>
            <Updates campaignData={campaign} />
          </Box>

          <Box id="comments" sx={{ marginBottom: "30px" }}>
            <Comments campaignData={campaign} />
          </Box>

          <Box sx={{ marginBottom: "30px" }}>
            <Donors campaignData={campaign} />
          </Box>
        </Box>

        <Box sx={{ marginBottom: isMobile ? "30px" : "0px" }}>
          <ReferFriend campaignData={campaign} />
        </Box>
      </Box>

      {isMobile && (
        <Box sx={{ width: "100%", marginBottom: "30px" }}>
          <UrgentNeedsCampaign campaignData={campaign} />
        </Box>
      )}

        <Box sx={{ width: "94.44%", maxWidth: "1440px", margin: "0 auto" }}>
        {!isMobile && (
          <Box sx={{ marginBottom: "15px" }}>
            <UrgentNeedsCampaign campaignData={campaign} />
          </Box>
        )}
        <Box sx={{ marginBottom: isMobile ? "0px" : "15px" }}>
          <Faq />
        </Box>
      </Box>

      {!isMobile && (
        <Box sx={{ marginBottom: "0px" }}>
          <NewsLogoSection />
        </Box>
      )}

      {isMobile && (
        <Box sx={{ marginBottom: "0px" }}>
          <HowToDonate isMedicalPage={true} />
        </Box>
      )}

      <CampaignPopup isOpen={showPopup} onClose={handleClosePopup} campaignData={campaign} />

      <Box
        data-donation-box="true"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: isMobile ? "0px" : "10px 0",
        }}
        >
        <DonationBox
          campaignTitle={campaign.title}
          campaignId={campaign.id}
          onDonationAmountChange={setDonationAmount}
        />
      </Box>
    </>
  );
};

export default LifeDetailPage;